// eslint-disable-next-line import/no-deprecated
import {Ago} from '@github-ui/ago'
import type {Repository} from '@github-ui/current-repository'
import {activityIndexPath} from '@github-ui/paths'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {BranchName, Label, Truncate} from '@primer/react'

import {useBaseUrl} from '../contexts/BaseUrlContext'
import {usePerPage} from '../contexts/PerPageContext'
import type {Commit, Push} from '../types/activity-types'
import styles from './ActivityCard.module.css'
import {ActivityOverflowMenu} from './ActivityOverflowMenu'
import {Pusher} from './Pusher'

const refPrefix = 'refs/heads/'
const tagPrefix = 'refs/tags/'

export function ActivityCard({
  push,
  repo,
  showRefInfo,
  showFullShas,
}: {
  push: Push
  repo: Repository
  showRefInfo: boolean
  showFullShas: boolean
}) {
  return (
    <div className={styles.card}>
      <div>
        <div className={styles.headerRow}>
          <h2 data-testid="push-link" className="h5">
            {activityActionMessage(push)}
          </h2>
          <div className={styles.pushLabels} data-testid="push-labels">
            {activityLabels(push)}
          </div>
        </div>
        <div className={styles.mutedText}>
          {push.pusher && <Pusher pusher={push.pusher} />}
          <ActionText push={push} repo={repo} showRefInfo={showRefInfo} />
          {showFullShas ? (
            <>
              <ActivityDate push={push} />
              <FullShaCompareText push={push} />
            </>
          ) : (
            <>
              <CompareText push={push} />
              <ActivityDate push={push} />
            </>
          )}
        </div>
      </div>
      <div className={styles.mobileOverflowMenu}>
        <ActivityOverflowMenu push={push} />
      </div>
      <div className={styles.desktopOverflowMenu}>
        <ActivityOverflowMenu push={push} />
      </div>
    </div>
  )
}

function activityLabels(push: Push) {
  const labels: React.ReactElement[] = []

  if (push.pushType === 'force_push') {
    labels.push(
      <Label key="force-push" variant="attention">
        Force push
      </Label>,
    )
  }

  if (push.pushType === 'pr_merge') {
    labels.push(
      <Label key="pr-merge" variant="done">
        Pull request merge
      </Label>,
    )
  }

  if (push.after && !push.commit) {
    labels.push(
      <Label key="missing-commit" variant="severe">
        Missing commit
      </Label>,
    )
  }

  return labels
}

function activityActionMessage(push: Push) {
  if (push.pushType === 'merge_queue_merge') {
    return 'Merge queue merge'
  } else if (push.pushType === 'branch_deletion') {
    if (push.ref.startsWith(tagPrefix)) {
      return 'Deleted tag'
    } else {
      return 'Deleted branch'
    }
  } else if (push.commit?.message) {
    return pushCommitMessage({commit: push.commit})
  } else if (push.pushType === 'branch_creation') {
    return 'Created branch'
  } else {
    return 'Updated branch'
  }
}

function ActivityDate({push}: {push: Push}) {
  const actionDate = push.pushedAt
  return actionDate ? (
    <span data-testid="push-date" style={{display: 'inline-block'}}>
      &nbsp;&bull;&nbsp;
      <Ago timestamp={new Date(actionDate)} />
    </span>
  ) : null
}

function ActionText({push, repo, showRefInfo}: {push: Push; repo: Repository; showRefInfo: boolean}) {
  const baseUrl = useBaseUrl()
  const perPage = usePerPage()
  const refName = push.ref.startsWith(refPrefix) ? push.ref.substring(refPrefix.length) : push.ref
  let action = ''
  let message = ''

  if (push.aheadBehind && push.aheadBehind.length > 0) {
    const [ahead, behind] = push.aheadBehind

    if (ahead !== 0 && behind === 0) {
      message = `adding ${ahead} ${pluralize(ahead!)}`
    } else if (ahead === 0 && behind !== 0) {
      message = `removing ${behind} ${pluralize(behind!)}`
    } else {
      message = `removing ${behind} ${pluralize(behind!)} and adding ${ahead} ${pluralize(ahead!)}`
    }
  }

  if (push.pushType === 'branch_deletion') {
    const isTag = push.ref.startsWith(tagPrefix)
    action = showRefInfo ? 'deleted' : `deleted this ${isTag ? 'tag' : 'branch'}`
  } else if (push.pushType === 'branch_creation') {
    action = showRefInfo ? 'created' : 'created this branch'
  } else if (push.pushType === 'force_push') {
    action = showRefInfo ? 'force pushed to' : 'force pushed'
  } else if (!message && push.commitsCount != null) {
    // skip this case if we have ahead/behind information
    action = showRefInfo
      ? `pushed ${push.commitsCount} ${pluralize(push.commitsCount)} to`
      : `pushed ${push.commitsCount} ${pluralize(push.commitsCount)}`
  } else {
    action = showRefInfo ? 'pushed to' : 'pushed'
  }

  return (
    <span data-testid="action-text">
      {action}
      {showRefInfo && (
        <>
          {' '}
          <BranchName href={activityIndexPath({repo, baseUrl, branch: refName, perPage})}>{refName}</BranchName>
        </>
      )}
      {message && <> {message}</>}
    </span>
  )
}

function pluralize(count: number) {
  return count === 1 ? 'commit' : 'commits'
}

function pushCommitMessage({commit}: {commit: Commit}) {
  return commit.shortMessageHtmlLink ? (
    <Truncate title={commit.message} className={styles.truncate}>
      <VerifiedHTMLDiv className={styles.commitMessage} html={commit.shortMessageHtmlLink} />
    </Truncate>
  ) : (
    // Edge case, should never happen (in theory)
    // But if it happens we should at least show something
    commit.message
  )
}

function CompareText({push}: {push: Push}) {
  let compare = ''

  if (push.before && push.after) {
    compare = `${shortSha(push.before)}…${shortSha(push.after)}` // push
  } else if (push.before && !push.after) {
    compare = '' // deleted - Leaving this empty case here for completeness
  } else if (!push.before && push.after) {
    compare = `${shortSha(push.after)}` // created
  }

  return compare ? (
    <span data-testid="compare-text">
      &nbsp;&bull;&nbsp;
      {compare}
    </span>
  ) : null
}

function FullShaCompareText({push}: {push: Push}) {
  const emptySha = '0000000000000000000000000000000000000000'

  return (
    <div data-testid="compare-text">
      {push.before || emptySha}&nbsp;&rarr;&nbsp;{push.after || emptySha}
    </div>
  )
}

function shortSha(sha: string) {
  return sha.slice(0, 7)
}
